/**
 * Typed async wrapper over the sim worker postMessage protocol.
 * Combines the old WorkerBridge + PreviewBridge into one class.
 */
import type { DiagramSpec } from "@diagram/diagram-view";
import type { InputDescriptor } from "@diagram/sim-model";
import type {
  InitRequest,
  SimCommand,
  SimMessage,
  SimStatus,
  TaggedMetricSnapshot,
} from "./protocol";

interface InitResult {
  topology: DiagramSpec;
  metricOwnership: Record<string, string[]>;
}

export interface SnapshotResult {
  simTime: number;
  metrics: TaggedMetricSnapshot[];
  haltReason?: string;
}

interface PreviewResult {
  topology: DiagramSpec;
}

/**
 * The minimal slice of the DOM `Worker` interface that {@link SimWorkerBridge}
 * actually uses. A real `Worker` structurally satisfies this.
 *
 * Why this exists: it lets a non-`Worker` object — notably the app's
 * `RelayWorker`, which hosts the real worker inside a sandboxed iframe and
 * forwards the sim protocol — be injected via `createWorker` without
 * pretending to implement the entire `Worker` API (terminate semantics,
 * event-target methods, message ports, etc.).
 */
export interface WorkerLike {
  postMessage(message: unknown): void;
  terminate(): void;
  // Method-style (bivariant) signatures, not property arrows: a real `Worker`
  // types these handlers with full DOM event objects (`MessageEvent`,
  // `ErrorEvent`), which are wider than what the bridge reads. Method-style
  // declarations let `Worker` structurally satisfy `WorkerLike` while the
  // bridge only touches `event.data` / `event.message`.
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
}

/**
 * Options for {@link SimWorkerBridge} beyond message callbacks.
 *
 * SECURITY — `createWorker` is load-bearing and deliberately REQUIRED (there
 * is no default). The worker this factory returns is where untrusted user /
 * shared simulation code is evaluated via `new Function(...)` (see
 * `worker.ts`). That worker's ORIGIN is the entire isolation boundary:
 *
 *   - A same-origin `Worker` (e.g. `new Worker(new URL("./worker.ts", …))`)
 *     runs the untrusted code at the app origin, where it can read the
 *     user's `localStorage` (Jazz account secret, API key) and IndexedDB
 *     (cached project data). That is a credential/data breach.
 *   - The app therefore ALWAYS injects `RelayWorker`, which hosts the worker
 *     inside a `sandbox="allow-scripts"` (opaque-origin, never
 *     `allow-same-origin`) iframe, so `localStorage` is absent and
 *     `indexedDB.open` throws `SecurityError`.
 *
 * A convenience same-origin default used to exist to support an embed
 * probe/fallback that was since deleted; nothing needs it now. Requiring the
 * factory means a future caller cannot silently fall back to an app-origin
 * worker — the type checker forces a conscious choice of worker context.
 * Any factory added here MUST produce an isolated/opaque-origin worker.
 *
 * The bridge's postMessage protocol is factory-agnostic; the factory returns
 * a {@link WorkerLike}, so a real `Worker` and the relay both satisfy it.
 */
export interface SimWorkerBridgeOptions {
  createWorker: () => WorkerLike;
}

export class SimWorkerBridge {
  private worker: WorkerLike;
  private onStatusChange?: (status: SimStatus) => void;
  private onSnapshot?: (snap: SnapshotResult) => void;
  private onTopology?: (spec: DiagramSpec) => void;
  private onMetricOwnership?: (map: Record<string, string[]>) => void;
  private onPreviewResult?: (spec: DiagramSpec) => void;
  private onPreviewError?: (msg: string) => void;
  private onInputsRegistered?: (inputs: InputDescriptor[]) => void;
  private onError?: (msg: string) => void;
  private pendingInit?: {
    resolve: (result: InitResult) => void;
    reject: (err: Error) => void;
  };
  private pendingSnapshot?: {
    resolve: (snap: SnapshotResult) => void;
    reject: (err: Error) => void;
    promise: Promise<SnapshotResult>;
  };
  private pendingPreview?: {
    resolve: (result: PreviewResult) => void;
    reject: (err: Error) => void;
  };

  constructor(
    callbacks: {
      onStatusChange?: (status: SimStatus) => void;
      onSnapshot?: (snap: SnapshotResult) => void;
      onTopology?: (spec: DiagramSpec) => void;
      onMetricOwnership?: (map: Record<string, string[]>) => void;
      onPreviewResult?: (spec: DiagramSpec) => void;
      onPreviewError?: (msg: string) => void;
      onInputsRegistered?: (inputs: InputDescriptor[]) => void;
      onError?: (msg: string) => void;
    },
    options: SimWorkerBridgeOptions,
  ) {
    this.onStatusChange = callbacks.onStatusChange;
    this.onSnapshot = callbacks.onSnapshot;
    this.onTopology = callbacks.onTopology;
    this.onMetricOwnership = callbacks.onMetricOwnership;
    this.onPreviewResult = callbacks.onPreviewResult;
    this.onPreviewError = callbacks.onPreviewError;
    this.onInputsRegistered = callbacks.onInputsRegistered;
    this.onError = callbacks.onError;

    // No same-origin default: see SimWorkerBridgeOptions — the factory must
    // return an isolated (opaque-origin) worker for untrusted sim code.
    this.worker = options.createWorker();
    this.worker.onmessage = (event: MessageEvent) => {
      // The protocol guarantees worker messages are SimMessage; the
      // WorkerLike interface keeps the event payload untyped so both a real
      // Worker and the relay satisfy it.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- worker→bridge messages are SimMessage by protocol
      this.handleMessage(event.data as SimMessage);
    };
    this.worker.onerror = (event) => {
      this.onError?.(event.message);
      this.onStatusChange?.("error");
    };
  }

  /**
   * Evaluate JS source, create engine, extract topology.
   *
   * `inputValues` is the frontend's current input-state map. The worker
   * hydrates the freshly-built engine from it before emitting descriptors,
   * so user edits made pre-Start survive engine rebuilds. Pass `{}` when
   * there is nothing to seed.
   */
  async init(
    jsSource: string,
    inputValues: Record<string, number | boolean>,
  ): Promise<InitResult> {
    const request: InitRequest = { jsSource, inputValues };
    return new Promise((resolve, reject) => {
      this.pendingInit = { resolve, reject };
      this.send({ type: "init", ...request });
    });
  }

  /** Evaluate JS source, extract topology only (no engine). */
  async preview(jsSource: string): Promise<PreviewResult> {
    return new Promise((resolve, reject) => {
      this.pendingPreview = { resolve, reject };
      this.send({ type: "preview", jsSource });
    });
  }

  start(): void {
    this.onStatusChange?.("running");
    this.send({ type: "start" });
  }

  pause(): void {
    this.onStatusChange?.("paused");
    this.send({ type: "pause" });
  }

  step(): void {
    this.send({ type: "step" });
  }

  setSpeed(multiplier: number): void {
    this.send({ type: "setSpeed", multiplier });
  }

  /** Send a new value for a registered input to the worker. */
  setInputValue(id: string, value: number | boolean): void {
    this.send({ type: "setInput", id, value });
  }

  requestSnapshot(): Promise<SnapshotResult> {
    if (this.pendingSnapshot) return this.pendingSnapshot.promise;
    let savedResolve: ((snap: SnapshotResult) => void) | undefined;
    let savedReject: ((err: Error) => void) | undefined;
    const promise = new Promise<SnapshotResult>((res, rej) => {
      savedResolve = res;
      savedReject = rej;
    });
    // The Promise executor runs synchronously, so savedResolve/savedReject are set.
    if (!savedResolve || !savedReject)
      throw new Error("Promise executor did not run");
    this.pendingSnapshot = {
      resolve: savedResolve,
      reject: savedReject,
      promise,
    };
    this.send({ type: "requestSnapshot" });
    return promise;
  }

  terminate(): void {
    this.worker.terminate();
  }

  private send(cmd: SimCommand): void {
    this.worker.postMessage(cmd);
  }

  private handleMessage(msg: SimMessage): void {
    switch (msg.type) {
      case "initialized":
        this.handleInitialized(msg.topology, msg.metricOwnership);
        break;
      case "previewResult":
        this.handlePreviewResult(msg.topology);
        break;
      case "previewError":
        this.handlePreviewError(msg.message);
        break;
      case "snapshot":
        this.handleSnapshotWithTopology(msg);
        break;
      case "done":
        this.handleDoneWithTopology(msg);
        break;
      case "inputsRegistered":
        this.onInputsRegistered?.(msg.inputs);
        break;
      case "error":
        this.handleError(msg.message);
        break;
    }
  }

  private handleInitialized(
    topology: DiagramSpec,
    metricOwnership: Record<string, string[]>,
  ): void {
    this.onTopology?.(topology);
    this.onMetricOwnership?.(metricOwnership);
    this.onStatusChange?.("paused");
    this.pendingInit?.resolve({ topology, metricOwnership });
    this.pendingInit = undefined;
  }

  private handlePreviewResult(topology: DiagramSpec): void {
    this.onPreviewResult?.(topology);
    this.pendingPreview?.resolve({ topology });
    this.pendingPreview = undefined;
  }

  private handlePreviewError(message: string): void {
    this.onPreviewError?.(message);
    this.pendingPreview?.reject(new Error(message));
    this.pendingPreview = undefined;
  }

  private applyTopologyIfPresent(
    topology?: DiagramSpec,
    metricOwnership?: Record<string, string[]>,
  ): void {
    if (topology) {
      this.onTopology?.(topology);
    }
    if (metricOwnership) {
      this.onMetricOwnership?.(metricOwnership);
    }
  }

  private handleSnapshotWithTopology(msg: {
    simTime: number;
    metrics: TaggedMetricSnapshot[];
    topology?: DiagramSpec;
    metricOwnership?: Record<string, string[]>;
  }): void {
    this.applyTopologyIfPresent(msg.topology, msg.metricOwnership);
    this.onSnapshot?.({ simTime: msg.simTime, metrics: msg.metrics });
    this.pendingSnapshot?.resolve({
      simTime: msg.simTime,
      metrics: msg.metrics,
    });
    this.pendingSnapshot = undefined;
  }

  private handleDoneWithTopology(msg: {
    simTime: number;
    metrics: TaggedMetricSnapshot[];
    haltReason?: string;
    topology?: DiagramSpec;
    metricOwnership?: Record<string, string[]>;
  }): void {
    this.applyTopologyIfPresent(msg.topology, msg.metricOwnership);
    this.onSnapshot?.({
      simTime: msg.simTime,
      metrics: msg.metrics,
      haltReason: msg.haltReason,
    });
    this.onStatusChange?.("done");
  }

  private handleError(message: string): void {
    this.onError?.(message);
    this.onStatusChange?.("error");
    this.pendingInit?.reject(new Error(message));
    this.pendingInit = undefined;
    this.pendingSnapshot?.reject(new Error(message));
    this.pendingSnapshot = undefined;
    this.pendingPreview?.reject(new Error(message));
    this.pendingPreview = undefined;
  }
}
